import React from 'react';
import {
  Grid, FormControl, InputLabel, Select, MenuItem, OutlinedInput, TextField,
  InputAdornment, Checkbox, FormControlLabel, Alert, Link, CircularProgress, Stack, Box,
} from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { PillButton } from '../../../components/superAdminUi';
import SectionCard from '../../../components/common/SectionCard';
import LogoUpload from '../../../components/common/LogoUpload';
import GroupFormFields from '../../../components/common/GroupFormFields';
import * as GroupNames from '../../../components/common/GroupNames';
import {
  GROUP_TYPE_OPTIONS,
  shouldShowGroupGuid,
} from '../../../constants/groupSchoolFieldOptions';

// Step 1: who the group is, laid out as bordered section cards. For UK MATs the GIAS
// quick-start leads the Group details card, autofilling the manual fields and loading the
// trust's schools into the Schools step; the optional Director login card mirrors the
// retired standalone page; Classification comes from the shared GroupFormFields card; and a
// final card carries the logo and the test-account flag. Duplicate detection surfaces as a
// banner inside the Group details card.
export default function GroupDetailsStep({
  formik,
  customerTypes,
  storageLocations,
  duplicate,
  giasLoading,
  giasResult,
  onGiasGroupSearch,
  onGroupFieldEdit,
}) {
  const { values, errors, touched, handleChange, setFieldValue } = formik;
  const isOtherGroup = values.Group === 'Other';

  const handleGroupSelect = (value) => {
    setFieldValue('Group', value);
    if (value === 'Other') {
      setFieldValue('CustomGroup', '');
      setFieldValue('GroupName', '');
    } else {
      setFieldValue('GroupName', value);
    }
  };

  return (
    <Stack spacing={2}>
      <SectionCard title="Group details" icon={<GroupsOutlinedIcon fontSize="small" />}>
        <Grid container spacing={1.5}>
          {duplicate && duplicate.isDuplicate && (
            <Grid item xs={12}>
              <Alert severity="warning">
                A group with this {duplicate.matchedBy === 'groupUid' ? 'GUID' : 'name'} already exists
                {duplicate.existingName ? `: ${duplicate.existingName}` : ''}.
                {duplicate.existingId && (
                  <>
                    {' '}
                    <Link href={`/group-detail?matId=${duplicate.existingId}`}>View existing group</Link>
                  </>
                )}
              </Alert>
            </Grid>
          )}

          {/* Group Type sits beside the GUID lookup because it decides whether the lookup shows:
              only UK MATs have a GIAS Group GUID to autofill from. */}
          <Grid item xs={12} lg={6}>
            <FormControl fullWidth>
              <InputLabel id="register-group-type-label">Group Type</InputLabel>
              <Select
                labelId="register-group-type-label"
                label="Group Type"
                value={values.GroupType || ''}
                onChange={(e) => setFieldValue('GroupType', e.target.value)}
              >
                {GROUP_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {shouldShowGroupGuid(values.GroupType) && (
            <Grid item xs={12} lg={6}>
              <FormControl fullWidth>
                <InputLabel htmlFor="register-group-guid">Group GUID — autofill from GIAS</InputLabel>
                <OutlinedInput
                  id="register-group-guid"
                  label="Group GUID — autofill from GIAS"
                  placeholder="e.g. 10005675"
                  value={values.MatGroupUID || ''}
                  onChange={(e) => setFieldValue('MatGroupUID', e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      {giasLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <PillButton type="button" $variant="danger" onClick={onGiasGroupSearch}>Search</PillButton>
                      )}
                    </InputAdornment>
                  }
                />
              </FormControl>
            </Grid>
          )}

          {giasResult && (
            <Grid item xs={12}>
              <Alert severity="success">
                Found {giasResult.name || 'group'} · {giasResult.count} school(s) loaded to the Schools step
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} lg={6}>
            <FormControl fullWidth>
              <InputLabel id="register-group-group-label">Group</InputLabel>
              <Select
                labelId="register-group-group-label"
                label="Group"
                value={values.Group || ''}
                onChange={(e) => handleGroupSelect(e.target.value)}
              >
                {GroupNames.getGroupNameCollection().map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {isOtherGroup && (
            <Grid item xs={12} lg={6}>
              <TextField
                fullWidth
                label="Custom Group Name"
                value={values.CustomGroup || ''}
                onChange={(e) => {
                  setFieldValue('CustomGroup', e.target.value);
                  setFieldValue('GroupName', e.target.value);
                }}
                error={Boolean(touched.GroupName && errors.GroupName)}
                helperText={touched.GroupName && errors.GroupName}
              />
            </Grid>
          )}

          <Grid item xs={12} lg={6}>
            <TextField
              fullWidth
              label="Group Name"
              name="Name"
              value={values.Name || ''}
              onChange={(e) => {
                handleChange(e);
                // Editing the group name clears any pinned group-scoped create error.
                if (onGroupFieldEdit) onGroupFieldEdit();
              }}
              error={Boolean(touched.Name && errors.Name)}
              helperText={touched.Name && errors.Name}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <TextField fullWidth label="First Name" name="FirstName" value={values.FirstName || ''} onChange={handleChange} />
          </Grid>

          <Grid item xs={12} lg={6}>
            <TextField fullWidth label="Last Name" name="LastName" value={values.LastName || ''} onChange={handleChange} />
          </Grid>
        </Grid>
      </SectionCard>

      {/* Optional director login. Leave blank to provision a dummy director as before;
          fill both to create the director account (parity with the retired standalone page). */}
      <SectionCard
        title="Director login (optional)"
        subtitle="Add an email and username to create the group director's login now, or leave blank."
        icon={<PersonOutlineIcon fontSize="small" />}
      >
        <Grid container spacing={1.5}>
          <Grid item xs={12} lg={6}>
            <TextField fullWidth label="Director Email" name="Email" value={values.Email || ''} onChange={handleChange} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <TextField fullWidth label="Director Username" name="Username" value={values.Username || ''} onChange={handleChange} />
          </Grid>
        </Grid>
      </SectionCard>

      {/* Country / Storage / Customer Type share the group edit modal's body (its own card);
          Group Type and the GUID stay above because this step leads with them for the GIAS autofill. */}
      <GroupFormFields
        values={values}
        onFieldChange={setFieldValue}
        customerTypeOptions={customerTypes}
        storageLocations={storageLocations}
        hideGroupTypeAndGuid
      />

      <SectionCard title="Logo & account flags" icon={<TuneOutlinedIcon fontSize="small" />}>
        <LogoUpload
          label="Group logo"
          helperText="PNG or JPEG. Shown across the group's dashboards."
          fileName={values.TrancateFileName || values.FileName}
          base64={values.File}
          onFile={({ base64, fileName, truncated }) => {
            setFieldValue('File', base64);
            setFieldValue('FileName', fileName);
            setFieldValue('TrancateFileName', truncated);
          }}
        />
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={values.isTestAccount || false}
                onChange={(e) => setFieldValue('isTestAccount', e.target.checked)}
              />
            }
            label="Test Account"
          />
        </Box>
      </SectionCard>
    </Stack>
  );
}
